import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';

export interface HttpMisc {
    removeContentType?: boolean;
}

export interface HttpCancelableRequestResult<T> {
    completed: boolean;
    value: T | null;
}

export class HttpCancelableRequest<T> {
    constructor(private readonly _sourceFactory: () => Observable<T>) {}
    private _isCanceled: boolean = false;
    private _subscription: Subscription | null = null;
    private _resolve: ((result: HttpCancelableRequestResult<T>) => void) | null = null;
    private _execution: Promise<HttpCancelableRequestResult<T>> | null = null;

    get isCanceled(): boolean {
        return this._isCanceled;
    }

    execute(): Promise<HttpCancelableRequestResult<T>> {
        if (this._execution) {
            return this._execution;
        }

        this._execution = new Promise<HttpCancelableRequestResult<T>>((resolve, reject) => {
            if (this._isCanceled) {
                resolve({ completed: false, value: null });
                return;
            }

            this._resolve = resolve;

            let lastValue: T | null = null;

            this._subscription = this._sourceFactory().subscribe({
                next: (value) => {
                    lastValue = value;
                },
                error: (error) => {
                    this._clear();
                    reject(error);
                },
                complete: () => {
                    const requestCompleted = !this._isCanceled;
                    this._clear();
                    resolve({
                        completed: requestCompleted,
                        value: requestCompleted ? lastValue : null,
                    });
                },
            });
        });

        return this._execution;
    }

    cancel(): void {
        if (this._isCanceled) {
            return;
        }

        this._isCanceled = true;
        this._subscription?.unsubscribe();
        this._subscription = null;

        if (this._resolve) {
            const resolve = this._resolve;
            this._clear();
            resolve({ completed: false, value: null });
        }
    }

    private _clear(): void {
        this._resolve = null;
        this._subscription = null;
    }
}
export abstract class BaseRepository {
    protected constructor(protected httpClient: HttpClient) {}

    protected httpGet<T>(url: string, options?: { [key: string]: any }, misc?: HttpMisc): Observable<T> {
        return this.httpClient.get<T>(url, this._getOptions(options, misc));
    }

    protected httpGetWithCancel<T>(
        url: string,
        options?: { [key: string]: any },
        misc?: HttpMisc,
    ): HttpCancelableRequest<T> {
        return this._createCancelableRequest(() => this.httpGet<T>(url, options, misc));
    }

    protected httpPost<T, Tbody>(
        url: string,
        body: Tbody,
        options?: { [key: string]: any },
        misc?: HttpMisc,
    ): Observable<T> {
        return this.httpClient.post<T>(url, body, this._getOptions(options, misc));
    }

    protected httpPostWithCancel<T, Tbody>(
        url: string,
        body: Tbody,
        options?: { [key: string]: any },
        misc?: HttpMisc,
    ): HttpCancelableRequest<T> {
        return this._createCancelableRequest(() => this.httpPost<T, Tbody>(url, body, options, misc));
    }

    protected httpPut<T, Tbody>(
        url: string,
        body: Tbody,
        options?: { [key: string]: any },
        misc?: HttpMisc,
    ): Observable<T> {
        return this.httpClient.put<T>(url, body, this._getOptions(options, misc));
    }

    protected httpPutWithCancel<T, Tbody>(
        url: string,
        body: Tbody,
        options?: { [key: string]: any },
        misc?: HttpMisc,
    ): HttpCancelableRequest<T> {
        return this._createCancelableRequest(() => this.httpPut<T, Tbody>(url, body, options, misc));
    }

    protected httpPatch<T, Tbody>(url: string, body: Tbody, misc?: HttpMisc): Observable<T> {
        return this.httpClient.patch<T>(url, body, this._getOptions(undefined, misc));
    }

    protected httpPatchWithCancel<T, Tbody>(url: string, body: Tbody, misc?: HttpMisc): HttpCancelableRequest<T> {
        return this._createCancelableRequest(() => this.httpPatch<T, Tbody>(url, body, misc));
    }

    protected httpDelete<T>(url: string, options?: { [key: string]: any }, misc?: HttpMisc): Observable<T> {
        return this.httpClient.delete<T>(url, this._getOptions(options, misc));
    }

    protected httpDeleteWithCancel<T>(
        url: string,
        options?: { [key: string]: any },
        misc?: HttpMisc,
    ): HttpCancelableRequest<T> {
        return this._createCancelableRequest(() => this.httpDelete<T>(url, options, misc));
    }

    private _createCancelableRequest<T>(sourceFactory: () => Observable<T>): HttpCancelableRequest<T> {
        return new HttpCancelableRequest<T>(sourceFactory);
    }

    private _getOptions(options?: any, misc?: HttpMisc): { [key: string]: any } {
        const defaultOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        };

        if (options) {
            // get all keys specified in options except for headers; headers will be dealt with separately
            const { headers, ...optionsWithoutHeaders } = options;

            // use those keys to override the default options
            Object.assign(defaultOptions, optionsWithoutHeaders);

            // copy headers now; for each header that was specified
            //   if a key is present then check the value for that key;
            //   if a key is not present then append it
            if (headers) {
                headers.keys().forEach((key) => {
                    const value = headers.get(key);

                    if (defaultOptions.headers.has(key)) {
                        if (value === undefined) {
                            defaultOptions.headers = defaultOptions.headers.delete(key);
                        } else {
                            defaultOptions.headers = defaultOptions.headers.set(key, value);
                        }
                    } else {
                        defaultOptions.headers = defaultOptions.headers.append(key, value);
                    }
                });
            }
        }

        if (misc?.removeContentType) {
            defaultOptions.headers = defaultOptions.headers.delete('Content-Type');
        }

        return defaultOptions;
    }
}
